import { useCallback, useEffect, useId, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { PRINCIPLE_TAGS } from "../../teacher-settings";
import { Conflict, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { Issue, Json, ModeEntry, RecommendationRow } from "../types";

/**
 * FS-15-03 모드 정의·추천 매핑 — SCR v0.6 sfs-15-03 · SR-CAT-02 · SR-MODE-08.
 *  - 모드 목록(활성 버전 배지 · 초안) · '＋ 새 모드 정의'
 *  - 정의 편집기(JSON · 스키마 검증 = POST …/modes/validate) · 버전 목록 · 롤백(옛 버전 다시 배포) · 배포(초안 저장 → publish)
 *    배포된 버전은 고칠 수 없다 — 고치면 다음 버전 초안으로 저장한다. 상태: 검증 실패 · 배포됨
 *  - 추천 매핑 표(원리 태그 · 모드 · 프리셋 · 가중치 · 출처) — 행 추가/삭제 · 전체 저장(PUT)
 * 배포 = 신규 룸부터(진행 중 룸은 이전 정의 유지).
 */
type Check = { ok: boolean; errors: Issue[] } | null;

export function parseDefinition(
  text: string,
): { ok: true; value: Json } | { ok: false; message: string } {
  try {
    return { ok: true, value: JSON.parse(text) as Json };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}

const activeOf = (m: ModeEntry) => m.versions.find((v) => v.enabled) ?? null;
const latestOf = (m: ModeEntry) => m.versions[m.versions.length - 1] ?? null;

export function ModesTab({
  api,
  onSummary,
}: {
  api: OpsApi;
  onSummary: (n: number, active: number) => void;
}) {
  const t = useT();
  const id = useId();
  const [modes, setModes] = useState<ModeEntry[] | null>(null);
  const [sel, setSel] = useState<{ modeId: string; version: number } | null>(null);
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState("");
  const [check, setCheck] = useState<Check>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);

  const loadModes = useCallback(async () => {
    try {
      const m = await api.modes();
      setModes(m);
      onSummary(m.length, m.filter((x) => activeOf(x)).length);
      return m;
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      return [];
    }
  }, [api, onSummary]);

  const open = useCallback(
    async (modeId: string, version: number) => {
      setSel({ modeId, version });
      setCheck(null);
      setNotice(null);
      setFailure(null);
      try {
        const def = await api.modeDefinition(modeId, version);
        const s = JSON.stringify(def, null, 2);
        setText(s);
        setLoaded(s);
      } catch (e) {
        setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      }
    },
    [api],
  );

  useEffect(() => {
    void loadModes().then((m) => {
      const first = m[0];
      const v = first && latestOf(first);
      if (first && v) void open(first.modeId, v.version);
    });
  }, [loadModes, open]);

  if (!modes) return <p className="pf-muted">{t("common.loading")}</p>;
  const mode = modes.find((m) => m.modeId === sel?.modeId) ?? null;
  const version = mode?.versions.find((v) => v.version === sel?.version) ?? null;
  const published = !!version?.publishedAt;
  const nextVersion = mode ? (latestOf(mode)?.version ?? 0) + 1 : 1;
  const active = mode ? activeOf(mode) : null;
  const dirty = text !== loaded;
  const parsed = parseDefinition(text);
  // 롤백 = 지금 활성보다 앞선, 배포된 적 있는 버전 중 가장 최근 것
  const rollback =
    mode && active
      ? [...mode.versions].reverse().find((v) => v.version < active.version && v.publishedAt)
      : null;

  async function validate(): Promise<boolean> {
    if (!parsed.ok) {
      setCheck({
        ok: false,
        errors: [{ path: "/", message: t("ops.modes.jsonError", { msg: parsed.message }) }],
      });
      return false;
    }
    setBusy(true);
    try {
      const r = await api.validateMode(parsed.value);
      setCheck(r);
      return r.ok;
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** 초안 저장 — 배포된 버전을 고쳤으면 다음 버전으로. 저장된 버전을 돌려준다 */
  async function saveDraft(): Promise<number | null> {
    if (!sel || !parsed.ok) {
      await validate();
      return null;
    }
    const target = published ? nextVersion : sel.version;
    setBusy(true);
    setFailure(null);
    try {
      await api.saveMode(sel.modeId, target, parsed.value);
      setCheck({ ok: true, errors: [] });
      await loadModes();
      setSel({ modeId: sel.modeId, version: target });
      setLoaded(text);
      setNotice(t("ops.modes.saved", { v: target }));
      return target;
    } catch (e) {
      if (e instanceof ValidationError) setCheck({ ok: false, errors: e.issues });
      else if (e instanceof Conflict) setFailure("HTTP-409");
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function publish(v: number) {
    if (!sel) return;
    setBusy(true);
    setFailure(null);
    try {
      await api.publishMode(sel.modeId, v);
      await loadModes();
      setNotice(t("ops.modes.published", { v }));
    } catch (e) {
      if (e instanceof ValidationError) setCheck({ ok: false, errors: e.issues });
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  async function deploy() {
    if (!sel) return;
    if (!(await validate())) return;
    const v = dirty || !version ? await saveDraft() : sel.version;
    if (v != null) await publish(v);
  }

  async function createMode(raw: string) {
    const modeId = raw.trim();
    if (!/^[a-z0-9-]{1,40}$/.test(modeId) || modes?.some((m) => m.modeId === modeId)) {
      setCheck({ ok: false, errors: [{ path: "/id", message: t("ops.modes.badId") }] });
      return;
    }
    // 새 정의는 지금 고른 정의를 본으로(id·version 만 바꿈) — 검증을 통과해야 저장된다
    const base =
      parsed.ok && parsed.value && typeof parsed.value === "object" && !Array.isArray(parsed.value)
        ? { ...parsed.value, id: modeId, version: 1 }
        : { id: modeId, version: 1 };
    const s = JSON.stringify(base, null, 2);
    setCreating(null);
    setSel({ modeId, version: 1 });
    setText(s);
    setLoaded("");
    setCheck(null);
  }

  return (
    <div className="pf-ops-modes" data-testid="ops-modes">
      <div className="pf-ops-split">
        <section className="pf-pane" aria-labelledby={`${id}-list`}>
          <h2 id={`${id}-list`} className="pf-h3">
            {t("ops.modes.list")}
          </h2>
          <ul className="pf-ops-list" data-testid="mode-list">
            {modes.map((m) => {
              const a = activeOf(m);
              const last = latestOf(m);
              const draft = last && !last.publishedAt;
              return (
                <li key={m.modeId}>
                  <button
                    type="button"
                    className={`pf-ops-item${sel?.modeId === m.modeId ? " pf-ops-item--on" : ""}`}
                    aria-current={sel?.modeId === m.modeId ? "true" : undefined}
                    data-mode={m.modeId}
                    onClick={() => last && void open(m.modeId, last.version)}
                  >
                    <span>{m.modeId}</span>
                    <span
                      className={`pf-chip-static ${a && !draft ? "pf-chip-static--ok" : "pf-chip-static--run"}`}
                    >
                      {a ? `v${a.version}` : "—"}
                      {draft ? ` · ${t("ops.modes.draftOf", { v: last.version })}` : ""}
                    </span>
                  </button>
                </li>
              );
            })}
            {sel && !mode && (
              <li>
                <span className="pf-ops-item pf-ops-item--on">
                  <span>{sel.modeId}</span>
                  <span className="pf-chip-static pf-chip-static--run">{t("ops.modes.new")}</span>
                </span>
              </li>
            )}
          </ul>
          {creating === null ? (
            <Button variant="ghost" className="pf-btn--sm" block onClick={() => setCreating("")}>
              {t("ops.modes.add")}
            </Button>
          ) : (
            <form
              className="pf-field-row"
              onSubmit={(e) => {
                e.preventDefault();
                void createMode(creating);
              }}
            >
              <label htmlFor={`${id}-new`} className="pf-sr-only">
                {t("ops.modes.newId")}
              </label>
              <input
                id={`${id}-new`}
                className="pf-input"
                value={creating}
                placeholder="mode-id"
                autoFocus
                onChange={(e) => setCreating(e.target.value)}
              />
              <Button type="submit" variant="accent" className="pf-btn--sm">
                {t("ops.modes.create")}
              </Button>
              <Button variant="ghost" className="pf-btn--sm" onClick={() => setCreating(null)}>
                {t("teacher.common.cancel")}
              </Button>
            </form>
          )}
        </section>

        <section
          className="pf-pane pf-pane--hero"
          aria-labelledby={`${id}-ed`}
          data-testid="mode-editor"
        >
          <div className="pf-editor-pane__head pf-row--wrap">
            <h2 id={`${id}-ed`} className="pf-h3">
              {sel
                ? published
                  ? t("ops.modes.headPublished", {
                      id: sel.modeId,
                      v: sel.version,
                      next: nextVersion,
                    })
                  : t("ops.modes.headDraft", { id: sel.modeId, v: sel.version })
                : "—"}
            </h2>
            {version && (
              <span className="pf-muted">
                {version.phase} · {t("ops.modes.tags")} {version.principleTags.join(", ") || "—"}
              </span>
            )}
          </div>
          {mode && (
            <div
              className="pf-row pf-row--wrap"
              role="group"
              aria-label={t("ops.modes.versions")}
              data-testid="mode-versions"
            >
              {mode.versions.map((v) => (
                <Button
                  key={v.version}
                  variant="ghost"
                  className="pf-btn--sm"
                  aria-pressed={v.version === sel?.version}
                  onClick={() => void open(mode.modeId, v.version)}
                >
                  v{v.version} ·{" "}
                  {v.enabled
                    ? t("ops.modes.stateActive")
                    : v.publishedAt
                      ? t("ops.modes.statePast")
                      : t("ops.modes.stateDraft")}
                </Button>
              ))}
            </div>
          )}
          <div className="pf-row pf-row--wrap">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={busy || !sel}
              data-testid="mode-validate"
              onClick={() => void validate()}
            >
              {t("ops.modes.validate")}
            </Button>
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={busy || !sel || !dirty}
              data-testid="mode-save"
              onClick={() => void saveDraft()}
            >
              {published ? t("ops.modes.saveAs", { v: nextVersion }) : t("ops.modes.save")}
            </Button>
            {rollback && (
              <Button
                variant="ghost"
                className="pf-btn--sm"
                disabled={busy}
                data-testid="mode-rollback"
                onClick={() => void publish(rollback.version)}
              >
                {t("ops.modes.rollback", { v: rollback.version })}
              </Button>
            )}
            <Button
              variant="accent"
              className="pf-btn--sm pf-row__end"
              disabled={busy || !sel || (!dirty && !!version?.enabled)}
              data-testid="mode-publish"
              onClick={() => void deploy()}
            >
              {t("ops.modes.publish")}
            </Button>
          </div>
          <label htmlFor={`${id}-json`} className="pf-sr-only">
            {t("ops.modes.editor")}
          </label>
          <textarea
            id={`${id}-json`}
            className="pf-input pf-input--code"
            value={text}
            spellCheck={false}
            aria-invalid={check && !check.ok ? true : undefined}
            aria-describedby={`${id}-check`}
            data-testid="mode-json"
            onChange={(e) => {
              setText(e.target.value);
              setCheck(null);
              setNotice(null);
            }}
          />
          <div id={`${id}-check`} aria-live="polite">
            {check?.ok && (
              <div className="pf-banner pf-banner--ok" data-testid="mode-check-ok">
                <p className="pf-banner__text">{t("ops.modes.checkOk")}</p>
              </div>
            )}
            {check && !check.ok && (
              <div
                className="pf-banner pf-banner--err pf-banner--list"
                role="alert"
                data-testid="mode-check-errors"
              >
                <p className="pf-banner__text">
                  <b>{t("ops.modes.checkFail", { n: check.errors.length })}</b>{" "}
                  <code className="pf-banner__code">(HTTP-422)</code>
                </p>
                <ul className="pf-note">
                  {check.errors.slice(0, 12).map((e, i) => (
                    <li key={i}>
                      <code>{e.path}</code> — {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {notice && (
              <p className="pf-note" role="status" data-testid="mode-notice">
                {notice}
              </p>
            )}
          </div>
          {failure && (
            <ErrorBanner
              cause={t("ops.common.saveFailed")}
              action={t("ops.common.retry")}
              code={failure}
            />
          )}
        </section>
      </div>
      <RecommendationTable api={api} modes={modes} />
      <p className="pf-note">{t("ops.modes.footer")}</p>
    </div>
  );
}

function RecommendationTable({ api, modes }: { api: OpsApi; modes: ModeEntry[] }) {
  const t = useT();
  const id = useId();
  const [rows, setRows] = useState<RecommendationRow[] | null>(null);
  const [saved, setSaved] = useState<string>("");
  const [issues, setIssues] = useState<Issue[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    api
      .recommendations()
      .then((r) => {
        if (off) return;
        setRows(r);
        setSaved(JSON.stringify(r));
      })
      .catch((e: unknown) => !off && setFailure(e instanceof ApiError ? e.code : "NET-SERVER"));
    return () => {
      off = true;
    };
  }, [api]);

  if (!rows)
    return failure ? (
      <ErrorBanner
        cause={t("ops.common.loadFailed")}
        action={t("ops.common.retry")}
        code={failure}
      />
    ) : null;
  const dirty = JSON.stringify(rows) !== saved;
  const put = (i: number, p: Partial<RecommendationRow>) =>
    setRows((xs) => xs && xs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const rowErr = (i: number) => issues.filter((x) => x.path.startsWith(`/items/${i}`));
  const tags = [...new Set([...PRINCIPLE_TAGS, ...rows.map((r) => r.principleTag)])];

  async function save() {
    if (!rows) return;
    setBusy(true);
    setIssues([]);
    setFailure(null);
    try {
      const r = await api.saveRecommendations(rows);
      setRows(r);
      setSaved(JSON.stringify(r));
    } catch (e) {
      if (e instanceof ValidationError) setIssues(e.issues);
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="pf-pane" aria-labelledby={`${id}-h`} data-testid="recommendations">
      <div className="pf-row">
        <h2 id={`${id}-h`} className="pf-h3">
          {t("ops.rec.title")}
        </h2>
        <Button
          variant="ghost"
          className="pf-btn--sm pf-row__end"
          onClick={() =>
            setRows([
              ...rows,
              {
                principleTag: PRINCIPLE_TAGS[0],
                modeId: modes[0]?.modeId ?? "",
                presetKey: null,
                weight: 0.5,
                source: "manual",
              },
            ])
          }
        >
          {t("ops.rec.add")}
        </Button>
        <Button
          variant="accent"
          className="pf-btn--sm"
          disabled={!dirty || busy}
          data-testid="rec-save"
          onClick={() => void save()}
        >
          {t("ops.rec.save")}
        </Button>
      </div>
      <table className="pf-tbl">
        <thead>
          <tr>
            <th scope="col">{t("ops.rec.colTag")}</th>
            <th scope="col">{t("ops.rec.colMode")}</th>
            <th scope="col">{t("ops.rec.colPreset")}</th>
            <th scope="col">{t("ops.rec.colWeight")}</th>
            <th scope="col">{t("ops.rec.colSource")}</th>
            <th scope="col">
              <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const errs = rowErr(i);
            const n = i + 1;
            return (
              <tr key={i} className={errs.length ? "pf-tbl__row--warn" : undefined} data-row={i}>
                <td>
                  <select
                    className="pf-input"
                    aria-label={t("ops.rec.aTag", { n })}
                    value={r.principleTag}
                    onChange={(e) => put(i, { principleTag: e.target.value })}
                  >
                    {tags.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="pf-input"
                    aria-label={t("ops.rec.aMode", { n })}
                    value={r.modeId}
                    onChange={(e) => put(i, { modeId: e.target.value })}
                  >
                    {[...new Set([...modes.map((m) => m.modeId), r.modeId])].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="pf-input pf-input--num"
                    aria-label={t("ops.rec.aPreset", { n })}
                    value={r.presetKey ?? ""}
                    placeholder="—"
                    onChange={(e) => put(i, { presetKey: e.target.value.trim() || null })}
                  />
                </td>
                <td>
                  <input
                    className="pf-input pf-input--num"
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    aria-label={t("ops.rec.aWeight", { n })}
                    aria-invalid={errs.some((x) => x.path.endsWith("weight")) || undefined}
                    value={r.weight}
                    data-testid="rec-weight"
                    onChange={(e) =>
                      put(i, {
                        weight: e.target.value === "" ? Number.NaN : Number(e.target.value),
                      })
                    }
                  />
                </td>
                <td>
                  <select
                    className="pf-input"
                    aria-label={t("ops.rec.aSource", { n })}
                    value={r.source}
                    onChange={(e) =>
                      put(i, { source: e.target.value === "worksheet" ? "worksheet" : "manual" })
                    }
                  >
                    <option value="worksheet">worksheet</option>
                    <option value="manual">manual</option>
                  </select>
                  {errs.length > 0 && (
                    <span className="pf-field-err" role="alert">
                      {" "}
                      {errs.map((x) => x.message).join(" · ")}
                    </span>
                  )}
                </td>
                <td className="pf-tbl__act">
                  <Button
                    variant="ghost"
                    className="pf-btn--sm"
                    aria-label={t("ops.rec.remove", { n })}
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  >
                    ✕
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {issues.length > 0 && (
        <ErrorBanner
          cause={t("ops.rec.invalid", { n: issues.length })}
          action={t("ops.config.invalidNext")}
          code="HTTP-422"
        />
      )}
      {failure && (
        <ErrorBanner
          cause={t("ops.common.saveFailed")}
          action={t("ops.common.retry")}
          code={failure}
        />
      )}
    </section>
  );
}
